import { useCallback, useMemo } from 'react'
import { useStore } from 'store'
import { type Escopo, escopoParaStore, storeParaEscopo } from './escopo'

type Opcoes = {
    /**
     * Escrever a escolha de funcionário em `employeeIds`.
     * Telas de seleção única devem usar `false`
     * para não propagar o filtro de funcionário único.
     */
    propagarFuncionarios?: boolean
}

/**
 * Liga o escopo no store: o que o controle escolhe passa a valer para os
 * relatórios, que já filtram as linhas por `useFilterLocaisCentroStore`.
 */
export function useEscopoFuncionario({
    propagarFuncionarios = true,
}: Opcoes = {}): [Escopo, (proximo: Escopo) => void] {
    const companyId = useStore(state => state.companyId)
    const costCenterIds = useStore(state => state.costCenterIds)
    const locaisIds = useStore(state => state.locaisIds)
    const employeeIds = useStore(state => state.employeeIds)
    const employeeId = useStore(state => state.employeeId)
    const incluirDemitidos = useStore(state => state.incluirDemitidos)
    const setCompanyId = useStore(state => state.setCompanyId)
    const setCostCenterIds = useStore(state => state.setCostCenterIds)
    const setLocaisIds = useStore(state => state.setLocaisIds)
    const setEmployeeIds = useStore(state => state.setEmployeeIds)
    const setEmployeeId = useStore(state => state.setEmployeeId)
    const setIncluirDemitidos = useStore(state => state.setIncluirDemitidos)

    const funcionariosEscopo = useMemo(
        () =>
            propagarFuncionarios
                ? employeeIds
                : employeeId > 0
                  ? [employeeId]
                  : [],
        [propagarFuncionarios, employeeIds, employeeId],
    )

    const escopo = useMemo(
        () =>
            storeParaEscopo({
                companyId,
                costCenterIds,
                locaisIds,
                employeeIds: funcionariosEscopo,
                incluirDemitidos,
            }),
        [
            companyId,
            costCenterIds,
            locaisIds,
            funcionariosEscopo,
            incluirDemitidos,
        ],
    )

    const onChange = useCallback(
        (proximo: Escopo) => {
            const alvo = escopoParaStore(proximo, { propagarFuncionarios })
            if (alvo.companyId !== companyId && alvo.companyId > 0)
                setCompanyId(alvo.companyId)
            setCostCenterIds(alvo.costCenterIds)
            setLocaisIds(alvo.locaisIds)
            if (propagarFuncionarios) setEmployeeIds(alvo.employeeIds)
            // -1 também chega no store, se não ocorre fallback
            else setEmployeeId(alvo.employeeId)
            setIncluirDemitidos(alvo.incluirDemitidos)
        },
        [
            companyId,
            propagarFuncionarios,
            setCompanyId,
            setCostCenterIds,
            setLocaisIds,
            setEmployeeIds,
            setEmployeeId,
            setIncluirDemitidos,
        ],
    )

    return [escopo, onChange]
}
